/* ═══════════════════════════════════════════════════════════════
   The Designer — the Sections palette (the Manual rail).

   One project is one Manual of sections. The rail is the set-level
   view of it: every section in CSI order with an issuance-aware
   status, click to open. Switching sections never leaves the surface.

   R5: this is a PALETTE now. Hiding and showing it is the panel's job
   (its dock tab, its floating panel, or Tools ▸ Sections), which is why
   the rail no longer carries a collapse toggle of its own — and why the
   show/hide state now follows the user across machines with the rest of
   the dock layout instead of living in one browser's localStorage.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import EmptyState from '../components/EmptyState';
import type { ManualSection } from './useDesignerDoc';

export interface ManualRailProps {
  sections: ManualSection[];
  activeId: string;
  onOpen: (specId: string) => void;
  /** Back to the project's Specs list — where sections are added/removed. */
  onManageSections?: () => void;
}

const STATUS_LABEL: Record<ManualSection['status'], string> = {
  'not-issued': 'Not issued',
  clean: 'Issued',
  edited: 'Edited since issue',
};

/** Status is never colour-only: each chip states its meaning in words. */
function StatusChip({
  status,
  issuanceName,
}: {
  status: ManualSection['status'];
  issuanceName?: string;
}) {
  const label = status === 'clean' && issuanceName ? issuanceName : STATUS_LABEL[status];
  return (
    <span className={`dsg-chip dsg-chip-${status}`} title={STATUS_LABEL[status]}>
      {label}
    </span>
  );
}

const SectionsIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 4h11a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4z" />
    <path d="M8 8h6M8 12h6" />
  </svg>
);

export function ManualRail({ sections, activeId, onOpen, onManageSections }: ManualRailProps) {
  const [query, setQuery] = React.useState('');
  const listRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(
      (s) => s.csiNumber.toLowerCase().includes(q) || s.title.toLowerCase().includes(q)
    );
  }, [sections, query]);

  // Arrow keys walk the list — the rail is a real listbox, not a div soup.
  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[data-rail-item]') || []
    );
    const idx = items.findIndex((el) => el === document.activeElement);
    const next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
    items[Math.max(0, Math.min(items.length - 1, next))]?.focus();
  };

  return (
    <aside className="dsg-rail" aria-label="Sections in this manual">
      <div className="dsg-rail-search">
        <label className="sr-only" htmlFor="dsg-rail-filter">
          Filter sections
        </label>
        <input
          id="dsg-rail-filter"
          type="search"
          className="dsg-input"
          placeholder="28 31 00"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div
        ref={listRef}
        className="dsg-rail-list"
        role="listbox"
        aria-label="Sections"
        onKeyDown={onListKeyDown}
      >
        {filtered.length === 0 ? (
          <div className="dsg-rail-empty">
            <EmptyState
              icon={SectionsIcon}
              title={sections.length ? 'No section matches that' : 'No sections yet'}
              subtitle={
                sections.length
                  ? 'Clear the filter to see the whole manual.'
                  : 'Add sections from your firm’s masters to start the manual.'
              }
              {...(sections.length === 0 && onManageSections
                ? { action: { label: 'Add sections', onClick: onManageSections } }
                : {})}
            />
          </div>
        ) : (
          filtered.map((s) => {
            const active = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                data-rail-item
                role="option"
                aria-selected={active}
                className={`dsg-rail-item${active ? ' is-active' : ''}`}
                onClick={() => onOpen(s.id)}
              >
                <span className="dsg-rail-csi">{s.csiNumber || '—'}</span>
                <span className="dsg-rail-name">{s.title}</span>
                <StatusChip status={s.status} issuanceName={s.issuanceName} />
              </button>
            );
          })
        )}
      </div>

      {onManageSections && sections.length > 0 && (
        <footer className="dsg-rail-foot">
          <button type="button" className="dsg-link-btn" onClick={onManageSections}>
            Add or remove sections
          </button>
        </footer>
      )}
    </aside>
  );
}
